import React, { useEffect, useState } from 'react';
import VideoCall from './video-call/VideoCall';
import LogoLoader from './LogoLoader';
import styled from 'styled-components';
import { useAuth0 } from '@auth0/auth0-react';
import useAuthenticatedAxios from '../auth/AuthenticatedAxios';

const VideoCallTabContainer = styled.div`
  height: 100%;
  width: 100%;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  display: flex;
  flex-direction: column;
  position: relative;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #33445e;
  font-size: 16px;
  gap: 15px;
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #33445e;
  text-align: center;
  padding: 40px 20px;
  gap: 20px;
`;

const ErrorTitle = styled.h3`
  margin: 0;
  color: #192947;
  font-size: 20px;
  font-weight: 500;
`;

const ErrorMessage = styled.p`
  margin: 0;
  color: #455776;
  font-size: 14px;
  line-height: 1.5;
  max-width: 400px;
`;

const ErrorButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, #335cf4, #2d6bb8);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.25s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(51, 92, 244, 0.3);
  }
`;

const VideoCallSetupContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: #33445e;
  text-align: center;
  padding: 40px 20px;
  gap: 25px;
`;

const SetupIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, rgba(51, 92, 244, 0.25), rgba(51, 92, 244, 0.15));
  border: 2px solid rgba(51, 92, 244, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(51, 92, 244, 0.15);
  backdrop-filter: blur(10px);
  
  &::before {
    content: "📹";
    font-size: 28px;
  }
`;

const SetupTitle = styled.h3`
  margin: 0;
  color: #192947;
  font-size: 22px;
  font-weight: 500;
`;

const SetupDescription = styled.p`
  margin: 0;
  color: #455776;
  font-size: 15px;
  line-height: 1.5;
  max-width: 450px;
`;



const ComingSoonBadge = styled.div`
  background: linear-gradient(135deg, rgba(51, 92, 244, 0.2), rgba(51, 92, 244, 0.3));
  border: 1px solid rgba(51, 92, 244, 0.5);
  color: #335cf4;
  padding: 10px 20px;
  border-radius: 25px;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  box-shadow: 0 4px 15px rgba(51, 92, 244, 0.2);
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 12px;
  }
`;

const FeatureList = styled.div`
  margin-top: 30px;
  text-align: left;
  
  @media (max-width: 768px) {
    margin-top: 20px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  color: #455776;
  font-size: 14px;
  gap: 10px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 10px;
  }
`;

const VideoCallTab = ({ conversation, expertId, videoCallParams }) => {
  const { isAuthenticated } = useAuth0();
  const [callConfig, setCallConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSetup, setShowSetup] = useState(true);
  const authenticatedAxios = useAuthenticatedAxios();

  // Auto-start video call if videoCallParams are provided (from URL)
  useEffect(() => {
    if (videoCallParams && videoCallParams.expertId && videoCallParams.channelName && isAuthenticated) {
      // Auto-start the call
      const autoStartCall = async () => {
        try {
          setIsLoading(true);
          setError(null);
          setShowSetup(false); // Skip setup form

          // Get call configuration from server
          const response = await authenticatedAxios.post(`/api/video-call/join-room`, {
            expertId: videoCallParams.expertId,
            channelName: conversation.threadId
          });

          if (response.status !== 200) {
            throw new Error('Failed to get call configuration');
          }

          const resData = await response.data;
          const config = {
            appId: process.env.REACT_APP_AGORA_APP_ID,
            channelName: conversation.threadId,
            token: resData.data.token,
            uid: resData.data.uid,
            expertName: resData.data.expert.name
          };
          
          setCallConfig(config);
          setIsLoading(false);

        } catch (err) {
          console.error('Error auto-starting video call:', err);
          setError('Failed to join video call. Please check your connection and try again.');
          setIsLoading(false);
          setShowSetup(true); // Show setup form on error
          
          // For localhost testing, add a test mode option
          console.log('If testing locally, you can use test mode by adding ?test=true to the URL');
        }
      };

      autoStartCall();
    }
  }, [videoCallParams, isAuthenticated, authenticatedAxios]);



  const handleCallEnd = (duration) => {
    console.log(`Call ended after ${duration} seconds`);
    setCallConfig(null);
    setError(null);
    
    // Always show waiting message after call ends (users can't initiate new calls)
    setShowSetup(true);
  };

  const handleError = (error) => {
    console.error('Video call error:', error);
    setError('Video call encountered an error. Please try again.');
    setCallConfig(null);
    
    // For auto-started calls, show error without going back to setup
    if (videoCallParams) {
      setShowSetup(false);
    } else {
      setShowSetup(true);
    }
  };

  const resetCall = () => {
    setCallConfig(null);
    setShowSetup(true);
    setError(null);
  };

  if (!isAuthenticated) {
    return (
      <VideoCallTabContainer>
        <ErrorContainer>
          <SetupIcon style={{ '&::before': { content: '"🔒"' } }} />
          <ErrorTitle>Authentication Required</ErrorTitle>
          <ErrorMessage>Please log in to access the video call feature.</ErrorMessage>
        </ErrorContainer>
      </VideoCallTabContainer>
    );
  }

  if (isLoading) {
    return (
      <VideoCallTabContainer>
        <LoadingContainer>
          <LogoLoader size={160} message="Starting your video call" />
        </LoadingContainer>
      </VideoCallTabContainer>
    );
  }

  if (error && !showSetup) {
    return (
      <VideoCallTabContainer>
        <ErrorContainer>
          <SetupIcon style={{ '&::before': { content: '"⚠️"' } }} />
          <ErrorTitle>Error</ErrorTitle>
          <ErrorMessage>{error}</ErrorMessage>
          <ErrorButton onClick={resetCall}>
            Try Again
          </ErrorButton>
        </ErrorContainer>
      </VideoCallTabContainer>
    );
  }

  if (callConfig && !showSetup) {
    return (
      <VideoCallTabContainer>
        <VideoCall
          appId={callConfig.appId}
          channelName={callConfig.channelName}
          token={callConfig.token}
          uid={callConfig.uid}
          expertName={callConfig.expertName}
          onCallEnd={handleCallEnd}
          onError={handleError}
        />
      </VideoCallTabContainer>
    );
  }

  // Show waiting message when no video call params are provided (manual tab access)
  if (showSetup && !videoCallParams) {
    return (
      <VideoCallTabContainer>
        <VideoCallSetupContainer>
          <SetupIcon />
          <SetupTitle>Video Call</SetupTitle>
          <SetupDescription>
            Video calls can only be initiated by experts. You will automatically join when an expert sends you a video call invitation link.
          </SetupDescription>
          
          <ComingSoonBadge>Waiting for Expert Invitation</ComingSoonBadge>
          
          <FeatureList>
            <FeatureItem>📞 Click on expert's video call link to join</FeatureItem>
            <FeatureItem>🎥 High-quality video conferencing</FeatureItem>
            <FeatureItem>🎤 Crystal clear audio communication</FeatureItem>
            <FeatureItem>📱 Works on desktop and mobile</FeatureItem>
          </FeatureList>
        </VideoCallSetupContainer>
      </VideoCallTabContainer>
    );
  }

  // Show waiting message when video call params are provided but no call config yet
  if (videoCallParams && !callConfig && !error) {
    return (
      <VideoCallTabContainer>
        <VideoCallSetupContainer>
          <SetupIcon />
          <SetupTitle>Joining Video Call</SetupTitle>
          <SetupDescription>
            Connecting to video call with {videoCallParams.expertId}...
          </SetupDescription>
        </VideoCallSetupContainer>
      </VideoCallTabContainer>
    );
  }

  return null;
};

export default VideoCallTab; 